import { useCallback, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import prettyBytes from 'pretty-bytes';
import { UploadCloud, X } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { useCompleteSyslog, useCreateRecording, usePresignSyslog, useUploadConfig } from '../api';
import { detectType } from '../lib/detectType';
import { xhrUpload } from '../lib/xhrUpload';
import type { DetectionResult, UploadConfig } from '../types';

type Phase = 'idle' | 'fileSelected' | 'uploading' | 'succeeded' | 'failed';

interface SelectedFile {
  file: File;
  detection: DetectionResult;
}

export function UploadForm() {
  const navigate = useNavigate();
  const uploadConfig = useUploadConfig();
  const createRecording = useCreateRecording();
  const presignSyslog = usePresignSyslog();
  const completeSyslog = useCompleteSyslog();

  const [phase, setPhase] = useState<Phase>('idle');
  const [selected, setSelected] = useState<SelectedFile | null>(null);
  // Override is used only when detection returned 'ambiguous'. Otherwise the
  // detected type is the source of truth.
  const [typeOverride, setTypeOverride] = useState<'recording' | 'syslog' | null>(null);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!uploadConfig.data) return; // shouldn't happen — dropzone is disabled until config loads
      const file = acceptedFiles[0];
      if (!file) return;
      const detection = detectType(file, uploadConfig.data);
      setSelected({ file, detection });
      setTypeOverride(null);
      setPhase('fileSelected');
    },
    [uploadConfig.data],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: !uploadConfig.data || phase === 'uploading',
  });

  // Resolve the effective upload type — detection by default, override if the
  // user picked one in the ambiguous case.
  const effectiveType: 'recording' | 'syslog' | null = useMemo(() => {
    if (!selected) return null;
    if (selected.detection.type === 'recording') return 'recording';
    if (selected.detection.type === 'syslog') return 'syslog';
    if (selected.detection.type === 'ambiguous') return typeOverride;
    return null; // 'rejected'
  }, [selected, typeOverride]);

  const canSubmit =
    phase === 'fileSelected' &&
    effectiveType !== null &&
    selected?.detection.type !== 'rejected' &&
    !!uploadConfig.data;

  const handleCancel = () => {
    if (phase === 'uploading') {
      abortRef.current?.abort();
    }
    setSelected(null);
    setTypeOverride(null);
    setProgress(null);
    setPhase('idle');
  };

  const handleUpload = async () => {
    if (!selected || !effectiveType) return;

    setPhase('uploading');
    setProgress({ loaded: 0, total: selected.file.size });
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      if (effectiveType === 'recording') {
        await uploadRecording(selected.file, controller.signal);
      } else {
        await uploadSyslog(selected.file, controller.signal);
      }
      toast.success('Upload complete');
      setPhase('succeeded');
      navigate('/uploads');
    } catch (err) {
      const isAbort = err instanceof DOMException && err.name === 'AbortError';
      if (isAbort) {
        // Already reset in handleCancel; nothing more to do.
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Upload failed');
      setPhase('failed');
      // Reset back to fileSelected so the user can retry without re-picking.
      setProgress(null);
      setPhase('fileSelected');
    } finally {
      abortRef.current = null;
    }
  };

  async function uploadRecording(file: File, signal: AbortSignal) {
    await createRecording.mutateAsync({
      file,
      signal,
      onProgress: (loaded, total) => setProgress({ loaded, total }),
    });
  }

  async function uploadSyslog(file: File, signal: AbortSignal) {
    const presigned = await presignSyslog.mutateAsync({
      originalFilename: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    });

    // Direct PUT to storage. No Authorization header — the URL is the cred.
    // Content-Type MUST be the same MIME the presign locked into the signature,
    // or the PUT will be rejected by the storage backend.
    const putResult = await xhrUpload({
      url: presigned.url,
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      onProgress: (loaded, total) => setProgress({ loaded, total }),
      signal,
    });

    if (putResult.status < 200 || putResult.status >= 300) {
      throw new Error(`Upload to storage failed (HTTP ${putResult.status})`);
    }

    await completeSyslog.mutateAsync({ uploadId: presigned.uploadId });
  }

  return (
    <div className="max-w-2xl space-y-5">
      {uploadConfig.isLoading && <Skeleton className="h-32 w-full" />}
      {uploadConfig.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load upload limits</AlertTitle>
          <AlertDescription>
            The server didn&apos;t return the upload configuration. Try refreshing the page.
          </AlertDescription>
        </Alert>
      )}

      {uploadConfig.data && (
        <>
          <div
            {...getRootProps()}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-center transition-colors',
              isDragActive ? 'border-primary bg-primary/5' : 'border-slate-300',
              phase === 'uploading' && 'pointer-events-none opacity-60',
            )}
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="upload-new-file-input" />
            <UploadCloud className="mb-3 h-8 w-8 text-slate-400" aria-hidden="true" />
            <div className="text-sm text-slate-700">
              {isDragActive ? 'Drop the file here' : 'Drag a file here or click to browse'}
            </div>
            <SizeGuidance config={uploadConfig.data} />
          </div>

          {selected && (
            <SelectedFileInfo
              file={selected.file}
              detection={selected.detection}
              effectiveType={effectiveType}
              onOverride={setTypeOverride}
              override={typeOverride}
              disabled={phase === 'uploading'}
            />
          )}

          {selected?.detection.type === 'rejected' && (
            <Alert variant="destructive" data-testid="rejection-alert">
              <AlertTitle>Can&apos;t upload this file</AlertTitle>
              <AlertDescription>{selected.detection.reason}</AlertDescription>
            </Alert>
          )}

          {phase === 'uploading' && progress && (
            <div className="space-y-1" data-testid="upload-progress">
              <Progress value={progressPercent(progress)} />
              <div className="text-xs text-muted-foreground">
                {prettyBytes(progress.loaded)} of {prettyBytes(progress.total)} (
                {progressPercent(progress)}%)
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={handleCancel}
              disabled={phase === 'idle'}
              data-testid="upload-new-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={!canSubmit}
              data-testid="upload-new-submit"
              data-phase={phase}
            >
              {phase === 'uploading' ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function progressPercent(progress: { loaded: number; total: number }): number {
  if (!progress.total) return 0;
  return Math.min(100, Math.round((progress.loaded / progress.total) * 100));
}

function SizeGuidance({ config }: { config: UploadConfig }) {
  return (
    <div className="mt-2 space-y-0.5 text-xs text-slate-500">
      <div>
        Recordings: max {prettyBytes(config.recording.maxBytes)} (
        {config.recording.mimePatterns.join(', ')})
      </div>
      <div>
        Syslogs: max {prettyBytes(config.syslog.maxBytes)} ({config.syslog.extensions.join(', ')})
      </div>
    </div>
  );
}

function SelectedFileInfo({
  file,
  detection,
  effectiveType,
  onOverride,
  override,
  disabled,
}: {
  file: File;
  detection: DetectionResult;
  effectiveType: 'recording' | 'syslog' | null;
  onOverride: (v: 'recording' | 'syslog') => void;
  override: 'recording' | 'syslog' | null;
  disabled: boolean;
}) {
  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium" data-testid="selected-filename">
            {file.name}
          </div>
          <div className="text-xs text-muted-foreground">
            {prettyBytes(file.size)}
            {effectiveType && ` · ${effectiveType}`}
          </div>
        </div>
        <X className="h-4 w-4 text-slate-400" aria-hidden="true" />
      </div>

      {detection.type === 'ambiguous' && (
        <div className="space-y-2">
          <div className="text-sm text-slate-700">{detection.reason}</div>
          <RadioGroup
            className="flex gap-4"
            value={override ?? ''}
            onValueChange={(v) => onOverride(v as 'recording' | 'syslog')}
            disabled={disabled}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="recording" id="type-recording" />
              <Label htmlFor="type-recording">Recording</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="syslog" id="type-syslog" />
              <Label htmlFor="type-syslog">Syslog</Label>
            </div>
          </RadioGroup>
        </div>
      )}
    </div>
  );
}
